// @flow strict

import React from 'react';

import { Grid, Box } from '@material-ui/core';
import { GatsbyImage } from 'gatsby-plugin-image';

import { useLanguageContext } from '~contexts/language-context';
import { SocialMediaIcon } from '~components/icons/social-media/social-media-icon';
import { createLinkPath } from '~utils/helpers/create-link-path';

import type { Author } from '~types/author';

import * as styles from './author-list-item.module.less';

/**
 * Type for component properties.
 */
type AuthorListItemProps = {
  author: Author,
  displayReadAllLink?: boolean,
};

/**
 * Component to display author details in list.
 *
 * @param {AuthorListItemProps} props - properties
 *
 * @returns {Node}
 */
export const AuthorListItem = ({
  author,
  displayReadAllLink = false,
}: AuthorListItemProps) => {
  const { language, loc } = useLanguageContext();

  return (
    <section className={styles.author}>
      <Grid container spacing={6} alignItems="center">
        <Grid item xs={4} sm={3}>
          <figure>
            <GatsbyImage
              image={author.image.src.childImageSharp.gatsbyImageData}
              alt={author.image.alt}
              objectFit="contain"
              className={styles.image}
            />
          </figure>
        </Grid>

        <Grid item xs={8}>
          <div className={styles.authorContent}>
            <header className={styles.header}>
              <h1 className={styles.headline}>{author.name}</h1>
              <div className={styles.socialMediaList}>
                {Object.entries(author.socialMedia).map(([type, link]) => {
                  if (link == null || link === '') {
                    return null;
                  }

                  return (
                    <a
                      key={type}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {/* $FlowExpectedError - map does not resolve types */}
                      <SocialMediaIcon type={type} />
                    </a>
                  );
                })}
              </div>
            </header>

            {/* Hidden on mobile */}
            <Box display={{ xs: 'none', sm: 'block' }}>
              <p className={styles.description}>
                {author.descriptions[language]}
              </p>

              {displayReadAllLink && (
                <a
                  className={styles.readAll}
                  href={createLinkPath({
                    path: `/blog/authors/${author.path}`,
                    language,
                  })}
                >
                  {loc('_author_read_all')}
                </a>
              )}
            </Box>
          </div>
        </Grid>

        {/* mobile only */}
        <Box component={Grid} item xs={12} display={{ sm: 'none' }}>
          <p className={styles.description}>{author.descriptions[language]}</p>
          {displayReadAllLink && (
            <a
              className={styles.readAll}
              href={createLinkPath({
                path: `/blog/authors/${author.path}`,
                language,
              })}
            >
              {loc('_author_read_all')}
            </a>
          )}
        </Box>
      </Grid>
    </section>
  );
};
